import React, { useMemo, useState } from 'react';
import ConfirmDialog from '@components/Dialogs/ConfirmDialog';
import {
  getMultiSelectionState,
  useMultiSelectionActions,
} from '@components/MainStage/multiSelection.store';
import useStore from '@components/MainStage/store';
import { useProject } from '@context/Project/ProjectProvider';
import { useToast } from '@context/Toast/ToastProvider';
import useAssets from '@hooks/useAssets';
import useSaveEntitiesResponse from '@hooks/useSaveEntitiesResponse';
import { useDeleteNodesMutation } from '@store/services/nodes';
import {
  INodeActionOutcome,
  NodeActionResult,
  NodeDTO,
  NodeDTOTypes,
} from '@store/services/nodes/types';
import { parseErrorResponse } from '@utils/helpers';

import { DELETE_SELECTION_COPY } from './deleteSelection.constants';
import { DeletableNode, splitDeletableNodes } from './deleteSelection.helpers';
import { NodeActionConfirmModal } from './NodeActionConfirmModal';

export const useDeleteSelection = (
  targetIds: string[],
  onCloseMenu: () => void,
) => {
  const { getNodeById, nodes } = useStore();
  const {
    toolbox: { mode },
  } = useProject();
  const { selectedAssetId, resetAsset } = useAssets();
  const { setSelection } = useMultiSelectionActions();
  const { showToast } = useToast();
  const { saveResponseToRedux } = useSaveEntitiesResponse();
  const [deleteNodes, { isLoading }] = useDeleteNodesMutation();
  const [pendingOutcomes, setPendingOutcomes] = useState<
    INodeActionOutcome[] | null
  >(null);
  const [isAssetConfirmOpen, setIsAssetConfirmOpen] = useState(false);

  const targetIdsKey = targetIds.join(',');

  const targetNodes = useMemo(
    () =>
      targetIds
        .map((id) => getNodeById(id)?.data?.dto as NodeDTO | undefined)
        .filter((dto): dto is NodeDTO => Boolean(dto)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [targetIdsKey, nodes, getNodeById],
  );

  const { deletable } = useMemo(
    () =>
      mode
        ? splitDeletableNodes(
            targetNodes as DeletableNode[],
            mode,
            selectedAssetId,
          )
        : { deletable: [], ineligible: [] },
    [targetNodes, mode, selectedAssetId],
  );

  const assets = deletable.filter((node) => node.type === NodeDTOTypes.Asset);

  const nameById = useMemo(
    () => new Map(targetNodes.map((node) => [node.id, node.name])),
    [targetNodes],
  );

  const isVisible = deletable.length > 0;

  const pruneSelection = (deletedIds: string[]) => {
    const deleted = new Set(deletedIds);
    const { selectedIds } = getMultiSelectionState();
    setSelection(selectedIds.filter((id) => !deleted.has(id)));
  };

  const run = async (ids: string[], force: boolean) => {
    if (!mode) return;

    try {
      const response = await deleteNodes({
        nodeIds: ids,
        mode,
        assetId: selectedAssetId || undefined,
        force,
      }).unwrap();

      if (!response.applied) {
        const allBlocked =
          response.outcomes.length > 0 &&
          response.outcomes.every(
            (outcome) => outcome.result === NodeActionResult.Blocked,
          );

        if (allBlocked) {
          setPendingOutcomes(null);
          setIsAssetConfirmOpen(false);
          showToast(DELETE_SELECTION_COPY.allBlockedError, 'error');
          onCloseMenu();
          return;
        }

        setPendingOutcomes(response.outcomes);
        setIsAssetConfirmOpen(false);
        return;
      }

      if (response.nodes) {
        saveResponseToRedux(response);
      }

      const deletedIds = response.outcomes
        .filter((outcome) => outcome.result === NodeActionResult.Updated)
        .map((outcome) => outcome.node_id);

      pruneSelection(deletedIds);
      setPendingOutcomes(null);
      setIsAssetConfirmOpen(false);

      if (assets.length) {
        if (assets.some((asset) => asset.id === selectedAssetId)) resetAsset();
        showToast(DELETE_SELECTION_COPY.assetSuccess, 'success');
      } else {
        const count = response.deleted_count ?? deletedIds.length;
        showToast(DELETE_SELECTION_COPY.successWithCount(count), 'success');
      }

      onCloseMenu();
    } catch (e) {
      setPendingOutcomes(null);
      setIsAssetConfirmOpen(false);
      showToast(parseErrorResponse(e), 'error');
    }
  };

  const start = () => {
    if (isLoading || !isVisible) return;

    if (assets.length) {
      setIsAssetConfirmOpen(true);
      return;
    }

    run(targetIds, false);
  };

  const outcomes = pendingOutcomes ?? [];
  const toItem = (outcome: INodeActionOutcome) => ({
    id: outcome.node_id,
    name: nameById.get(outcome.node_id) ?? outcome.node_id,
  });

  const toChange = outcomes
    .filter((outcome) => outcome.result === NodeActionResult.Updated)
    .map(toItem);
  const skipped = outcomes
    .filter(
      (outcome) =>
        outcome.result === NodeActionResult.Ineligible ||
        outcome.result === NodeActionResult.Blocked,
    )
    .map(toItem);

  const allDeletable = !skipped.length;

  const modal = (
    <>
      <NodeActionConfirmModal
        open={pendingOutcomes !== null}
        title={DELETE_SELECTION_COPY.title}
        text={
          allDeletable
            ? DELETE_SELECTION_COPY.textAllDeletable(
                toChange.map(({ name }) => name),
              )
            : DELETE_SELECTION_COPY.text
        }
        toChange={toChange}
        toChangeTitle={DELETE_SELECTION_COPY.toChangeTitle}
        skipped={skipped}
        toSkipTitle={DELETE_SELECTION_COPY.toSkipTitle}
        confirmName={DELETE_SELECTION_COPY.confirmName}
        backName={
          allDeletable ? DELETE_SELECTION_COPY.backNameAllDeletable : undefined
        }
        showItemLists={!allDeletable}
        onBack={() => setPendingOutcomes(null)}
        onConfirm={() => run(targetIds, true)}
      />

      <ConfirmDialog
        open={isAssetConfirmOpen}
        onClose={() => setIsAssetConfirmOpen(false)}
        onBack={() => setIsAssetConfirmOpen(false)}
        onConfirm={() =>
          run(
            assets.map((asset) => asset.id),
            true,
          )
        }
        title={DELETE_SELECTION_COPY.title}
        text={DELETE_SELECTION_COPY.assetText}
        confirmName={DELETE_SELECTION_COPY.assetConfirmName}
      />
    </>
  );

  return { isVisible, isLoading, start, modal };
};
